import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { THEME } from '../../theme';

/** MAPF positions are [row, col]. Screen x is col, y is row (row 0 at top). */
export function toCell(pos) {
    if (!pos || pos.length < 2) return { row: 0, col: 0 };
    return { row: Number(pos[0]), col: Number(pos[1]) };
}

export const AGENT_PALETTE = [
    '#2cbb5d', '#38bdf8', '#f472b6', '#fbbf24', '#a78bfa',
    '#34d399', '#fb7185', '#22d3ee', '#eab308', '#c084fc',
];

function drawArrowHead(ctx, fromX, fromY, toX, toY, color, size = 8) {
    const angle = Math.atan2(toY - fromY, toX - fromX);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - size * Math.cos(angle - 0.4), toY - size * Math.sin(angle - 0.4));
    ctx.lineTo(toX - size * Math.cos(angle + 0.4), toY - size * Math.sin(angle + 0.4));
    ctx.closePath();
    ctx.fill();
}

/**
 * Animated grid showing robot paths from start to goal.
 * Positions are [row, col].
 */
export default function PathBoard({
    grid,
    paths,
    startPositions,
    goalPositions,
    step = 0,
    height = 560,
    title = 'Paths',
}) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const rows = grid?.length || 0;
        const cols = grid?.[0]?.length || 0;
        if (!rows || !cols) return;

        const dpr = window.devicePixelRatio || 1;
        const cssW = canvas.clientWidth || 720;
        const cssH = height;
        canvas.width = Math.floor(cssW * dpr);
        canvas.height = Math.floor(cssH * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const pad = 16;
        const legendH = 44;
        const titleH = 36;
        const boardW = cssW - pad * 2;
        const boardH = cssH - pad * 2 - legendH - titleH;
        const cell = Math.min(boardW / cols, boardH / rows);
        const gridW = cell * cols;
        const gridH = cell * rows;
        const ox = pad + (boardW - gridW) / 2;
        const oy = pad + titleH + (boardH - gridH) / 2;

        const cellCenter = (row, col) => ({
            x: ox + col * cell + cell / 2,
            y: oy + row * cell + cell / 2,
        });

        // Background
        ctx.fillStyle = THEME.bg;
        ctx.fillRect(0, 0, cssW, cssH);

        // Title
        ctx.fillStyle = THEME.primary;
        ctx.font = '600 13px "Fira Code", monospace';
        ctx.textAlign = 'left';
        ctx.fillText(title, pad, pad + 18);
        ctx.fillStyle = THEME.secondary;
        ctx.font = '12px system-ui, sans-serif';
        ctx.textAlign = 'right';
        const numSteps = paths?.length || 0;
        ctx.fillText(`step ${Math.min(step + 1, Math.max(numSteps, 1))} / ${numSteps || '—'}`, cssW - pad, pad + 18);

        // Floor vs obstacles
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const val = grid[r][c];
                const x = ox + c * cell;
                const y = oy + r * cell;
                if (val === 9999 || val === 1) {
                    ctx.shadowColor = '#ef4743';
                    ctx.shadowBlur = Math.max(4, cell * 0.35);
                    ctx.fillStyle = '#3a1515';
                    ctx.fillRect(x + 1, y + 1, cell - 2, cell - 2);
                    ctx.shadowBlur = 0;
                    ctx.strokeStyle = '#ef4743';
                    ctx.lineWidth = 1.25;
                    ctx.strokeRect(x + 1.5, y + 1.5, cell - 3, cell - 3);
                    ctx.fillStyle = 'rgba(239,71,67,0.35)';
                    ctx.fillRect(x + cell * 0.2, y + cell * 0.2, cell * 0.6, cell * 0.6);
                } else {
                    ctx.fillStyle = '#1a2228';
                    ctx.fillRect(x, y, cell, cell);
                    ctx.strokeStyle = 'rgba(44,187,93,0.22)';
                    ctx.lineWidth = 0.8;
                    ctx.strokeRect(x + 0.5, y + 0.5, cell - 1, cell - 1);
                    const grd = ctx.createRadialGradient(
                        x + cell / 2, y + cell / 2, 0,
                        x + cell / 2, y + cell / 2, cell * 0.55
                    );
                    grd.addColorStop(0, 'rgba(44,187,93,0.08)');
                    grd.addColorStop(1, 'rgba(44,187,93,0)');
                    ctx.fillStyle = grd;
                    ctx.fillRect(x, y, cell, cell);
                }
            }
        }

        const starts = startPositions?.length
            ? startPositions
            : paths?.[0] || [];
        const goals = goalPositions?.length
            ? goalPositions
            : (paths?.length ? paths[paths.length - 1] : []);
        const numAgents = Math.max(starts.length, goals.length, paths?.[0]?.length || 0);

        // Draw path lines and direction ticks up to the current step
        for (let a = 0; a < numAgents; a++) {
            const color = AGENT_PALETTE[a % AGENT_PALETTE.length];
            const trail = [];
            if (paths?.length) {
                const max = Math.min(step + 1, paths.length);
                for (let s = 0; s < max; s++) {
                    const p = paths[s]?.[a];
                    if (p) trail.push(toCell(p));
                }
            }
            if (trail.length >= 2) {
                ctx.strokeStyle = color;
                ctx.globalAlpha = 0.55;
                ctx.lineWidth = Math.max(2, cell * 0.18);
                ctx.lineJoin = 'round';
                ctx.lineCap = 'round';
                ctx.beginPath();
                trail.forEach((p, i) => {
                    const { x, y } = cellCenter(p.row, p.col);
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                });
                ctx.stroke();
                ctx.globalAlpha = 1;

                // Direction arrows along the trail
                for (let i = 1; i < trail.length; i++) {
                    const a0 = cellCenter(trail[i - 1].row, trail[i - 1].col);
                    const a1 = cellCenter(trail[i].row, trail[i].col);
                    if (a0.x === a1.x && a0.y === a1.y) continue;
                    const mx = (a0.x + a1.x) / 2;
                    const my = (a0.y + a1.y) / 2;
                    drawArrowHead(ctx, a0.x, a0.y, mx, my, color, Math.max(5, cell * 0.22));
                }
            }
        }

        // Starts
        for (let a = 0; a < starts.length; a++) {
            const { row, col } = toCell(starts[a]);
            const { x, y } = cellCenter(row, col);
            const color = AGENT_PALETTE[a % AGENT_PALETTE.length];
            const r = Math.max(6, cell * 0.32);
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(10,10,10,0.85)';
            ctx.fill();
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = color;
            ctx.stroke();
            ctx.fillStyle = color;
            ctx.font = `700 ${Math.max(9, cell * 0.28)}px system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('S', x, y);
        }

        // Goals
        for (let a = 0; a < goals.length; a++) {
            const { row, col } = toCell(goals[a]);
            const { x, y } = cellCenter(row, col);
            const color = AGENT_PALETTE[a % AGENT_PALETTE.length];
            const s = Math.max(10, cell * 0.55);
            const rr = 3;
            const hs = s / 2;
            ctx.beginPath();
            ctx.moveTo(x - hs + rr, y - hs);
            ctx.lineTo(x + hs - rr, y - hs);
            ctx.quadraticCurveTo(x + hs, y - hs, x + hs, y - hs + rr);
            ctx.lineTo(x + hs, y + hs - rr);
            ctx.quadraticCurveTo(x + hs, y + hs, x + hs - rr, y + hs);
            ctx.lineTo(x - hs + rr, y + hs);
            ctx.quadraticCurveTo(x - hs, y + hs, x - hs, y + hs - rr);
            ctx.lineTo(x - hs, y - hs + rr);
            ctx.quadraticCurveTo(x - hs, y - hs, x - hs + rr, y - hs);
            ctx.closePath();
            ctx.fillStyle = 'rgba(10,10,10,0.85)';
            ctx.fill();
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = color;
            ctx.setLineDash([3, 2]);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = color;
            ctx.font = `700 ${Math.max(9, cell * 0.28)}px system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('G', x, y);
        }

        // Agents at the current step
        if (paths?.length) {
            const frame = paths[Math.min(step, paths.length - 1)] || [];
            frame.forEach((pos, a) => {
                const { row, col } = toCell(pos);
                const { x, y } = cellCenter(row, col);
                const color = AGENT_PALETTE[a % AGENT_PALETTE.length];
                const r = Math.max(7, cell * 0.36);
                ctx.beginPath();
                ctx.arc(x, y, r, 0, Math.PI * 2);
                ctx.fillStyle = color;
                ctx.fill();
                ctx.lineWidth = 2;
                ctx.strokeStyle = '#0a0a0a';
                ctx.stroke();
                ctx.fillStyle = '#0a0a0a';
                ctx.font = `700 ${Math.max(10, cell * 0.32)}px system-ui, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(a + 1), x, y);
            });
        }

        // Key
        const ly = cssH - legendH + 16;
        ctx.font = '12px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        let lx = pad;
        const items = [
            { label: 'Start (S)', draw: (x, y) => { ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.strokeStyle = THEME.primary; ctx.stroke(); } },
            { label: 'Goal (G)', draw: (x, y) => { ctx.strokeStyle = THEME.primary; ctx.strokeRect(x - 6, y - 6, 12, 12); } },
            { label: 'Agent', draw: (x, y) => { ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.fillStyle = THEME.primary; ctx.fill(); } },
            { label: 'Path', draw: (x, y) => { ctx.strokeStyle = THEME.primary; ctx.beginPath(); ctx.moveTo(x - 8, y); ctx.lineTo(x + 8, y); ctx.stroke(); drawArrowHead(ctx, x - 8, y, x + 8, y, THEME.primary, 6); } },
        ];
        items.forEach((item) => {
            item.draw(lx + 8, ly);
            ctx.fillStyle = THEME.secondary;
            ctx.fillText(item.label, lx + 22, ly);
            lx += ctx.measureText(item.label).width + 48;
        });
    }, [grid, paths, startPositions, goalPositions, step, height, title]);

    if (!grid?.length) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', color: THEME.secondary }}>
                <Typography variant="body2">Run a solve to visualize paths.</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height,
                bgcolor: THEME.cardBg,
                border: `1px solid ${THEME.border}`,
                borderRadius: 2,
                overflow: 'hidden',
            }}
        >
            <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
        </Box>
    );
}
